import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarCheck, Loader2, PartyPopper } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { buildReservationWhatsApp } from "@/lib/site";
import { useLanguage } from "@/lib/i18n";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", guests: "2", seating: "Pé na areia", message: "" };

const inputClass =
  "w-full rounded-2xl border border-[#3D2B1F]/15 bg-[#FCFBF7] px-4 py-3 text-[#1A252C] placeholder:text-[#1A252C]/35 outline-none transition-all duration-300 focus:border-[#E76F51] focus:ring-4 focus:ring-[#E76F51]/10";

export default function ReservationSection() {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t.reservation.form.errorInvalid);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reservations`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        guests: parseInt(form.guests, 10),
        seating: form.seating,
        message: form.message.trim(),
      });
      toast.success(t.reservation.form.successToast);
      setConfirmed({ ...form });
    } catch (err) {
      toast.error(t.reservation.form.errorFail);
    } finally {
      setLoading(false);
    }
  };

  const seatingLabel = (id) =>
    t.reservation.form.seatingOptions.find((o) => o.id === id)?.label || id;

  const successView = (() => {
    if (!confirmed) return null;
    const guestsLabel =
      confirmed.guests === "10"
        ? t.reservation.form.guestGroup
        : `${confirmed.guests} ${t.reservation.success.people}`;
    return {
      title: t.reservation.success.title.replace("{name}", confirmed.name.split(" ")[0]),
      text: `${t.reservation.success.textPre} ${guestsLabel}, ${t.reservation.success.table} ${seatingLabel(confirmed.seating).toLowerCase()}. ${t.reservation.success.textPost}`,
    };
  })();

  return (
    <section
      id="reservar"
      data-testid="reservation-section"
      className="relative overflow-hidden bg-[#0D5C63] py-24 sm:py-32"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#2A9D8F]/30 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-[#E9C46A]/15 blur-3xl"
      />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="flex flex-col justify-center"
        >
          <p className="font-jetmono text-xs uppercase tracking-[0.25em] text-[#E9C46A]" data-testid="reservation-eyebrow">
            {t.reservation.eyebrow}
          </p>
          <h2 className="mt-4 font-playfair text-3xl leading-tight tracking-tight text-[#FAF6EE] sm:text-4xl lg:text-5xl">
            {t.reservation.titleA}{" "}
            <span className="italic text-[#E9C46A]">{t.reservation.titleB}</span>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-[#F4EBD9]/75 sm:text-lg">
            {t.reservation.subtitle}
          </p>
          <ul className="mt-8 space-y-3 font-jetmono text-xs uppercase tracking-[0.18em] text-[#F4EBD9]/60">
            {t.reservation.bullets.map((bullet) => (
              <li key={bullet} className="flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-[#E9C46A]" />
                {bullet}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE, delay: 0.1 }}
        >
          {confirmed && successView ? (
            <div
              data-testid="reservation-success"
              className="rounded-[2rem] border border-[#3D2B1F]/10 bg-[#FCFBF7] p-7 shadow-2xl shadow-[#082F38]/30 sm:p-9"
            >
              <div className="grid h-14 w-14 place-items-center rounded-full bg-[#E9C46A]/25 text-[#0D5C63]">
                <PartyPopper size={26} />
              </div>
              <h3 className="mt-5 font-cabinet text-2xl font-bold tracking-tight text-[#1A252C]">
                {successView.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#1A252C]/60">{successView.text}</p>
              <a
                data-testid="reservation-whatsapp-confirm"
                href={buildReservationWhatsApp(confirmed)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-6 py-3.5 font-cabinet font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:brightness-95"
              >
                <WhatsAppIcon size={18} />
                {t.reservation.success.whatsapp}
              </a>
              <button
                data-testid="reservation-new-button"
                onClick={() => {
                  setConfirmed(null);
                  setForm(EMPTY);
                }}
                className="mt-3 w-full rounded-full border border-[#3D2B1F]/15 px-6 py-3 font-cabinet font-bold text-[#1A252C]/70 transition-colors hover:border-[#E76F51] hover:text-[#E76F51]"
              >
                {t.reservation.success.again}
              </button>
            </div>
          ) : (
            <form
              data-testid="reservation-form"
              onSubmit={handleSubmit}
              className="rounded-[2rem] border border-[#3D2B1F]/10 bg-[#FCFBF7] p-7 shadow-2xl shadow-[#082F38]/30 sm:p-9"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="res-name" className="mb-2 block font-jetmono text-[11px] uppercase tracking-[0.18em] text-[#0D5C63]">
                    {t.reservation.form.name}
                  </label>
                  <input
                    id="res-name"
                    data-testid="reservation-name-input"
                    type="text"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder={t.reservation.form.namePh}
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="res-phone" className="mb-2 block font-jetmono text-[11px] uppercase tracking-[0.18em] text-[#0D5C63]">
                    {t.reservation.form.phone}
                  </label>
                  <input
                    id="res-phone"
                    data-testid="reservation-phone-input"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder={t.reservation.form.phonePh}
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="res-guests" className="mb-2 block font-jetmono text-[11px] uppercase tracking-[0.18em] text-[#0D5C63]">
                    {t.reservation.form.guests}
                  </label>
                  <select
                    id="res-guests"
                    data-testid="reservation-guests-select"
                    value={form.guests}
                    onChange={set("guests")}
                    className={inputClass}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                      <option key={n} value={n}>
                        {n} {n === 1 ? t.reservation.form.guestOne : t.reservation.form.guestMany}
                      </option>
                    ))}
                    <option value="10">{t.reservation.form.guestGroup}</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <span className="mb-2 block font-jetmono text-[11px] uppercase tracking-[0.18em] text-[#0D5C63]">
                    {t.reservation.form.seating}
                  </span>
                  <div className="flex flex-wrap gap-2" data-testid="reservation-seating-options">
                    {t.reservation.form.seatingOptions.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        data-testid={`seating-${opt.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => setForm((f) => ({ ...f, seating: opt.id }))}
                        className={`rounded-full border px-4 py-2 font-cabinet text-sm font-bold transition-all duration-300 ${
                          form.seating === opt.id
                            ? "border-[#E76F51] bg-[#E76F51] text-[#FAF6EE]"
                            : "border-[#3D2B1F]/15 text-[#1A252C]/60 hover:border-[#E76F51] hover:text-[#E76F51]"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="res-message" className="mb-2 block font-jetmono text-[11px] uppercase tracking-[0.18em] text-[#0D5C63]">
                    {t.reservation.form.message}
                  </label>
                  <textarea
                    id="res-message"
                    data-testid="reservation-message-input"
                    rows={3}
                    value={form.message}
                    onChange={set("message")}
                    placeholder={t.reservation.form.messagePh}
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>
              <button
                data-testid="reservation-submit-button"
                type="submit"
                disabled={loading}
                className="mt-7 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#E76F51] px-6 py-4 font-cabinet text-lg font-bold text-[#FAF6EE] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#D85C3D] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <CalendarCheck size={20} />
                )}
                {loading ? t.reservation.form.sending : t.reservation.form.submit}
              </button>
              <p className="mt-4 text-center text-xs text-[#1A252C]/45">
                {t.reservation.form.note}
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
